import { z } from 'zod';
import { agentChangesSchema } from './agent-change-schemas';
import { AGENT_TEMPLATES } from '../agent-templates';
import { AGENT_VOICES } from '../agent-voices';
import { actionParameterSchema, schemaFieldSchema } from './agent-draft/fields';
// Tools for building a real-time (voice and chat) agent in the editor. The browser runs them against the open
// agent's draft; the evals run the same logic against fixtures.
/** Voices the editor offers: ElevenLabs voices, less the one reserved for a single customer. */
const EDITOR_VOICES = Object.keys(AGENT_VOICES).filter((voice) => AGENT_VOICES[voice].voice.provider === '11labs' && voice !== 'Brandon');
export const viaVoiceSchema = z.enum(EDITOR_VOICES);
export const MAX_AGENT_NAME_LENGTH = 39;
const agentNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(MAX_AGENT_NAME_LENGTH)
    .describe(`The agent's name, at most ${MAX_AGENT_NAME_LENGTH} characters`);
const agentTemplateIds = AGENT_TEMPLATES.map(({ id }) => id);
/** For tools that read an agent: which one, opening its editor if needed. */
export const optionalAgentIdSchema = z
    .string()
    .optional()
    .describe("The agent's id. Defaults to the agent open in the editor. Any other agent is read from its saved draft without opening it.");
export const agentIdSchema = z
    .string()
    .describe("The agent's id. If its editor isn't showing, it's opened first.");
export const firstMessageModeSchema = z
    .enum(['agent-speaks-first', 'agent-waits-for-user'])
    .describe('agent-speaks-first: the agent says the message as soon as the conversation connects. agent-waits-for-user: the agent waits for the other person to speak first.');
const openingSchema = z.object({
    message: z.string().describe('What the agent says to open the conversation'),
    mode: firstMessageModeSchema
});
const channelInstructionsSchema = z.object({
    call: z.string().min(1).describe('Instructions for phone calls only'),
    chat: z.string().min(1).describe('Instructions for chat only')
});
const chatSuggestionsSchema = z.object({
    options: z.array(z.string()).min(1).describe('Replies shown as buttons in chat'),
    strict: z
        .boolean()
        .optional()
        .describe('Conversation Start only: the user must pick an option rather than type')
});
const knowledgeBaseRefSchema = z.object({
    knowledgeBaseId: z.string(),
    knowledgeBaseVersionId: z.string()
});
const chatComponentsSchema = z.object({
    datePicker: z.boolean(),
    addressInput: z.boolean(),
    options: z.boolean()
});
export const actionMessageSchema = z
    .object({
    mode: z.enum(['default', 'custom', 'none']),
    text: z.string().optional().describe('For custom: the words, five or six at most')
})
    .describe('What the agent says while the action runs. default: a short stock phrase such as "One moment". custom: your text. none: silent.');
const flowNodeViewSchema = z.object({
    id: z.string(),
    type: z
        .string()
        .describe('startNode (Conversation Start), agentTurnNode (Agent Turn) or endNode (Conversation End). handoffNode is deprecated; intakeNode and validateNode belong to claim intake agents. What an intakeNode asks is its form, in intakeForm.'),
    label: z.string(),
    instructions: z.string(),
    script: z
        .string()
        .describe('Said word for word when set. Empty on Conversation Start, whose message is in opening.'),
    notes: z.string(),
    channelInstructions: channelInstructionsSchema.optional(),
    chatSuggestions: chatSuggestionsSchema.optional(),
    opening: z
        .object({
        inbound: openingSchema.describe('Used for inbound calls and every chat'),
        outbound: openingSchema
            .nullable()
            .describe('Used for calls Strada places; null means the inbound one is used')
    })
        .optional()
        .describe('Conversation Start only'),
    outcomes: z.array(z.object({
        index: z.number(),
        text: z.string(),
        leadsTo: z
            .string()
            .optional()
            .describe('Id of the node this outcome connects to; missing if unconnected')
    }))
});
const actionViewSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    message: actionMessageSchema,
    variables: z.record(z.string(), actionParameterSchema),
    hasWorkflow: z.boolean().describe('Whether a workflow has been built for it')
});
export const agentDraftViewSchema = z.object({
    agentId: z.string(),
    name: z.string(),
    version: z
        .object({ versionId: z.string(), savedAt: z.string(), publishedAt: z.string().optional() })
        .optional()
        .describe('Set when you read a past version: the agent as it was then. Changes always go to the current draft'),
    unsavedChanges: z
        .boolean()
        .optional()
        .describe('Whether the editor holds changes not saved yet. Saved changes still reach callers only once published'),
    canEdit: z
        .boolean()
        .optional()
        .describe("Whether this user can change the agent. When false, tell them before planning or proposing any change, and who can give them access; don't plan changes they can't make"),
    persona: z.string(),
    objective: z.string(),
    nodes: z.array(flowNodeViewSchema),
    advancedRules: z.object({ all: z.string(), call: z.string(), chat: z.string() }),
    variables: z
        .array(z.string())
        .describe('Variables the prompt uses or declares. Any that are not built in must be passed in'),
    // Optional so reads from before it still parse
    builtInVariables: z
        .array(z.string())
        .optional()
        .describe("Variables every agent can use with nothing passed in, such as customer.number, the caller's phone number on a phone call"),
    callAnalysis: z.record(z.string(), schemaFieldSchema),
    settings: z.object({
        voice: z.string(),
        voiceSpeed: z.number().optional(),
        backgroundSound: z.enum(['off', 'office']),
        voicemail: z.string().nullable().describe('The voicemail message; null when voicemail is off'),
        dialKeypad: z.boolean(),
        knowledgeBase: knowledgeBaseRefSchema.nullable(),
        chatComponents: chatComponentsSchema,
        multilingual: z
            .array(z.string())
            .nullable()
            .describe('Languages a caller can ask to switch the call to; null when multilingual calls are off. Only Strada staff can change this.')
    }),
    actions: z.array(actionViewSchema),
    hasPostCallWorkflow: z.boolean(),
    intakeForm: z
        .object({
        blocks: z.array(z.object({
            id: z.string(),
            title: z.string(),
            askedWhen: z.string().describe('"Always", or the condition that decides it, in words'),
            fields: z.number()
        }))
    })
        .optional()
        .describe('On a claim intake agent: the form behind Gather Claim Info, a line per block. get_intake_form reads it in full, with every field and condition, and edit_intake_form changes it')
});
export const getAgentDraftToolSchema = {
    name: 'get_agent_draft',
    description: "Read an agent as its editor holds it, including unsaved edits: persona, objective, every conversation-flow node with its outcomes and where each leads, advanced rules, variables and the built-in ones every agent has (such as the caller's number), call analysis, voice and configuration, its actions and, on a claim intake agent, its form in outline. Call this before changing anything. With versionId, read the agent as it was in that version instead, such as the one a conversation ran on.",
    inputSchema: z.object({
        agentId: optionalAgentIdSchema,
        versionId: z
            .string()
            .optional()
            .describe("A version to read instead of the current draft, such as a conversation's agentVersionId")
    }),
    outputSchema: agentDraftViewSchema
};
export const textEditSchema = z.object({
    oldText: z
        .string()
        .min(1)
        .describe('The text to replace, copied exactly from what you read. It must appear exactly once in the field, so include enough of the words around it'),
    newText: z.string().describe('What goes in its place. Empty removes oldText')
});
const agentDraftFieldsSchema = z.object({
    role: z.string().optional().describe('The Persona field: who the agent is and how it sounds'),
    objective: z.string().optional().describe("The Objective field: the agent's purpose, first in its prompt"),
    advancedRules: z.string().optional().describe('Advanced Rules for every channel, at the end of the prompt'),
    advancedRulesCall: z.string().optional().describe('Advanced rules for calls only'),
    advancedRulesChat: z.string().optional().describe('Advanced rules for chat only'),
    variables: z
        .array(z.string())
        .optional()
        .describe('The full list of declared variables. Variables used in the text are added on save anyway, and built-in ones (builtInVariables in get_agent_draft) need no declaring.'),
    voice: viaVoiceSchema.optional().describe('Also resets the speed to that voice’s default'),
    voiceSpeed: z.number().min(0.8).max(1.2).optional(),
    backgroundSound: z.enum(['off', 'office']).optional().describe('office adds call-center background noise'),
    voicemail: z
        .string()
        .nullable()
        .optional()
        .describe('The message to leave on voicemail; null turns it off'),
    dialKeypad: z.boolean().optional().describe('Lets the agent press keys to navigate phone menus'),
    knowledgeBase: knowledgeBaseRefSchema.nullable().optional().describe('null detaches it'),
    callAnalysis: z
        .record(z.string(), schemaFieldSchema)
        .nullable()
        .optional()
        .describe('The full set of call analysis fields by name; null removes call analysis'),
    chatComponents: chatComponentsSchema.partial().optional().describe('Interactive components in chat')
});
export const agentDraftPatchSchema = agentDraftFieldsSchema.extend({
    edits: z
        .array(textEditSchema.extend({
        field: z.enum(['role', 'objective', 'advancedRules', 'advancedRulesCall', 'advancedRulesChat'])
    }))
        .optional()
        .describe('Changes to part of the persona, objective or advanced rules, applied in order. The rest of the field stays as it is. Use these for a small change instead of sending the whole field again')
});
export const agentDraftFieldSchema = agentDraftFieldsSchema.keyof();
export const updateAgentDraftToolSchema = {
    name: 'update_agent_draft',
    description: "Change an agent's settings outside the flow: persona, objective, advanced rules, variables, call analysis, voice, voicemail, dial keypad, knowledge base and chat components. Changes land in its unsaved draft. Only include the fields you're changing. A field you send replaces the whole field; to change part of the persona, objective or advanced rules, use edits. An edit changes its field or fails. If `changes` comes back without a field you edited, the user's page is older than this tool and nothing changed: ask them to refresh the page, and don't report the change as made.",
    inputSchema: z.object({
        agentId: agentIdSchema,
        patch: agentDraftPatchSchema
    }),
    outputSchema: z.object({
        agentId: z.string(),
        editId: z.string(),
        changes: z.array(z.object({ field: agentDraftFieldSchema, before: z.string(), after: z.string() })),
        // Optional so edits from before it still read
        diff: agentChangesSchema
            .optional()
            .describe('What the edit changed, area by area, as its card shows it: the words it touched and the steps it added, changed or removed')
    })
};
const flowNodeRefSchema = z
    .string()
    .describe('A node id from get_agent_draft, or the ref of a node added earlier in this batch');
export const flowOperationSchema = z.discriminatedUnion('op', [
    z.object({
        op: z.literal('add_node'),
        ref: z
            .string()
            .describe('A temporary name for the new node, so later operations in this batch can refer to it'),
        type: z.enum(['agentTurnNode', 'endNode']),
        label: z.string(),
        instructions: z.string().optional(),
        script: z.string().optional(),
        notes: z.string().optional(),
        channelInstructions: channelInstructionsSchema.optional(),
        outcomes: z.array(z.string()).optional().describe('Outcome descriptions; not allowed on endNode')
    }),
    z.object({
        op: z.literal('update_node'),
        node: flowNodeRefSchema,
        label: z.string().optional(),
        instructions: z
            .string()
            .optional()
            .describe('Replaces the whole text, as script and notes do. To change part of one, use replace_text'),
        script: z.string().optional().describe('Not on Conversation Start; use set_opening'),
        notes: z.string().optional(),
        channelInstructions: channelInstructionsSchema.nullable().optional().describe('null removes them'),
        chatSuggestions: chatSuggestionsSchema.nullable().optional().describe('null removes them')
    }),
    z
        .object({
        op: z.literal('replace_text'),
        node: flowNodeRefSchema,
        field: z
            .enum(['instructions', 'script', 'notes', 'callInstructions', 'chatInstructions'])
            .describe('callInstructions and chatInstructions are the channel-specific instructions'),
        ...textEditSchema.shape
    })
        .describe("Changes part of one of a node's texts. The rest of the text stays as it is"),
    z.object({
        op: z.literal('remove_node'),
        node: flowNodeRefSchema
    }),
    z.object({
        op: z.literal('set_outcomes'),
        node: flowNodeRefSchema,
        outcomes: z
            .array(z.string())
            .describe('The full new list. Existing connections stay attached by position; outcomes past the end lose theirs.')
    }),
    z.object({
        op: z.literal('connect'),
        from: flowNodeRefSchema,
        outcomeIndex: z.number().int().min(0),
        to: flowNodeRefSchema.describe('The node this outcome should lead to; replaces any existing connection')
    }),
    z.object({
        op: z.literal('set_opening'),
        inbound: openingSchema.describe('For inbound calls and every chat'),
        outbound: openingSchema
            .nullable()
            .optional()
            .describe('A separate opening for calls Strada places (campaigns, retries, test calls); null removes it so they use the inbound one')
    })
]);
export const editFlowToolSchema = {
    name: 'edit_flow',
    description: "Change an agent's conversation flow, as one batch that's applied all at once or not at all. Call get_agent_draft first for node ids. Changes land in its unsaved draft, and the user sees a summary with Undo. If a batch with replace_text fails with \"Validation failed\", the user's page is older than replace_text and the batch's other operations may have applied: ask them to refresh the page, then read the agent again before another change.",
    inputSchema: z.object({
        agentId: agentIdSchema,
        summary: z.string().describe('One short sentence describing the change, shown to the user'),
        operations: z.array(flowOperationSchema).min(1)
    }),
    outputSchema: z.object({
        agentId: z.string(),
        editId: z.string(),
        summary: z.string(),
        applied: z.array(z.string()).describe('What each operation did'),
        addedNodeIds: z.record(z.string(), z.string()).describe('Refs of added nodes mapped to their new ids'),
        diff: agentChangesSchema
            .optional()
            .describe('What the edit changed, area by area, as its card shows it: the words it touched and the steps it added, changed or removed')
    })
};
export const agentCheckIssueSchema = z.object({
    severity: z.enum(['error', 'warning', 'info']),
    where: z.string().describe('The part of the agent it concerns'),
    message: z.string(),
    fix: z.string().optional().describe('What to do about it, when the message does not already say')
});
export const checkAgentToolSchema = {
    name: 'check_agent',
    description: "Check an agent, including unsaved edits, for problems that would break it or make it behave badly, and measure the size of its prompt. Run it after building or changing an agent and before saving, and fix every error. It checks the draft as it is when it runs, so call it on its own once your edits are applied, never in the same step as an edit, whose change may still be waiting on the user. It doesn't change anything.",
    inputSchema: z.object({ agentId: optionalAgentIdSchema }),
    outputSchema: z.object({
        agentId: z.string(),
        promptTokens: z.object({
            call: z.number(),
            chat: z.number(),
            target: z.number().describe('Aim to stay under this'),
            max: z.number().describe('Only go past this for a very good reason')
        }),
        largestParts: z.array(z.object({ part: z.string(), tokens: z.number() })),
        knowledgeBaseAttached: z.boolean().describe('Its full text is added to the prompt on top of the count'),
        variables: z.array(z.string()).describe('Variables the prompt uses'),
        // Optional so checks from before it still parse
        variablesToPassIn: z
            .array(z.string())
            .optional()
            .describe('Those of them that are not built in, so a campaign, a pre-call workflow or the chat widget has to pass them in'),
        issues: z.array(agentCheckIssueSchema)
    })
};
export const createAgentToolSchema = {
    name: 'create_agent',
    description: `Create a new real-time agent and open it in the editor. Start from scratch, from a template, or as a copy of an existing agent. It exists as soon as this runs; build it up in its draft afterwards. Templates: ${AGENT_TEMPLATES.map(({ id, description }) => `${id} (${description})`).join('; ')}.`,
    inputSchema: z.object({
        name: agentNameSchema,
        template: z.enum(agentTemplateIds).optional(),
        copyOf: z.string().optional().describe('Id of an agent to copy instead; its latest draft is copied')
    }),
    outputSchema: z.object({ agentId: z.string(), name: z.string() })
};
export const renameAgentToolSchema = {
    name: 'rename_agent',
    description: "Rename an agent. Unlike other changes this applies straight away, without Save Draft or Publish, and doesn't affect calls.",
    inputSchema: z.object({ agentId: agentIdSchema, name: agentNameSchema }),
    outputSchema: z.object({ agentId: z.string(), name: z.string() })
};
export const listKnowledgeBasesToolSchema = {
    name: 'list_knowledge_bases',
    description: "List the organization's knowledge bases with their latest version, to attach one to an agent with update_agent_draft.",
    inputSchema: z.object({}),
    outputSchema: z.object({
        knowledgeBases: z.array(z.object({
            knowledgeBaseId: z.string(),
            name: z.string(),
            latestVersionId: z.string().optional().describe('Missing when it has no version yet')
        }))
    })
};
export const saveAgentDraftToolSchema = {
    name: 'save_agent_draft',
    description: "Save an agent's unsaved draft, as the Save Draft button does. Saving doesn't change live calls; publishing does. Its test cases run against the saved draft: after a change to what the agent says or does, check_agent_tests comes next.",
    inputSchema: z.object({ agentId: agentIdSchema }),
    outputSchema: z.object({ agentId: z.string(), versionId: z.string() })
};
export const publishAgentToolSchema = {
    name: 'publish_agent',
    description: "Publish an agent's saved draft. Everything that follows the latest published version switches to it from its next call or message; phone numbers pinned to a label don't (get_agent_deployment lists both). Save the draft first. The user confirms before it runs.",
    inputSchema: z.object({
        agentId: agentIdSchema,
        summary: z
            .string()
            .describe('What will change for live callers, in one or two sentences, shown to the user')
    }),
    outputSchema: z.object({ agentId: z.string() })
};
//# sourceMappingURL=agent-tool-schemas.js.map